import React, { useRef, useEffect, useMemo, useContext } from 'react'
import { useFragment, graphql } from 'react-relay'

import { lazy as loadable } from '@loadable/component'

import {
  differenceInCalendarDays,
  differenceInCalendarWeeks,
  differenceInCalendarYears,
} from 'date-fns'
import { format, utcToZonedTime } from 'date-fns-tz'

import { Strong } from '../Text'
import { EventItem } from './EventItem'
import { wrapSuspenseComponent } from '../SuspenseWrapper'

import { ServerContext } from '../../data/ServerContext'

import { TamaguiElement, YStack, XStack, Separator } from 'tamagui'

import { Spinner } from '../Spinner'

// TODO: Replace this with Object.groupBy when better supported by browsers & TypeScript
import groupBy from 'lodash.groupby'

import {
  EventList_eventsConnection$key,
  EventList_eventsConnection$data,
} from './__generated__/EventList_eventsConnection.graphql'
import {
  EventList_query$key,
  EventList_query$data,
} from './__generated__/EventList_query.graphql'

const NoEventsPlaceholder = wrapSuspenseComponent(
  loadable(() => import('./NoEventsPlaceholder'))
)

export const EventList = ({
  eventsConnection,
  query,
  hasNext,
  loadNext,
  showEndOfListPlaceholder = false,
  forceVertical,
}: {
  eventsConnection: EventList_eventsConnection$key
  query: EventList_query$key
  hasNext: boolean
  loadNext: (count: number) => any
  showEndOfListPlaceholder?: boolean
  forceVertical?: boolean
}) => {
  const data = useFragment(
    graphql`
      fragment EventList_eventsConnection on EventsConnection {
        edges {
          node {
            startAt

            ...EventItemNew_event
          }

          cursor
        }
      }
    `,
    eventsConnection
  )

  const queryData = useFragment(
    graphql`
      fragment EventList_query on Query {
        ...EventItemNew_query
      }
    `,
    query
  )

  const endOfListElement = useRef<TamaguiElement>(null)

  const serverContext = useContext(ServerContext)

  useEffect(() => {
    const currentEndOfListElement = endOfListElement.current

    if (
      !currentEndOfListElement ||
      !hasNext ||
      serverContext ||
      typeof window === 'undefined' ||
      !window.IntersectionObserver
    ) {
      return
    }

    const intersectionObserver = new window.IntersectionObserver((entries) => {
      const someIntersecting = entries.some((entry) => entry.isIntersecting)

      if (someIntersecting) {
        loadNext(10)
      }
    })

    intersectionObserver.observe(currentEndOfListElement as unknown as Element)

    return () => {
      intersectionObserver.disconnect()
    }
  }, [endOfListElement.current, data, hasNext, loadNext])

  const eventsGroupedByDay = useMemo(() => {
    if (!data.edges || !data.edges.length) {
      return null
    }

    return groupBy(data.edges, (edge) => {
      try {
        if (serverContext?.timeZone) {
          return format(
            utcToZonedTime(
              new Date(edge.node.startAt),
              serverContext?.timeZone
            ),
            'eeee, MMMM do'
          )
        }
      } catch (err) {
        // Fall back to formatting in the runtime timezone.
      }

      return format(new Date(edge.node.startAt), 'eeee, MMMM do')
    })
  }, [data])

  if (!eventsGroupedByDay) {
    return <NoEventsPlaceholder />
  }

  return (
    <YStack gap='$6'>
      {Object.keys(eventsGroupedByDay).map((date) => (
        <EventGroup
          key={date}
          queryData={queryData}
          date={date}
          group={eventsGroupedByDay[date]}
          forceVertical={forceVertical}
        />
      ))}

      {hasNext ? (
        <Spinner
          ref={endOfListElement as React.Ref<any>}
          size='$9'
          color='$primaryColor'
        />
      ) : (
        showEndOfListPlaceholder && <NoEventsPlaceholder marginTop='$12' />
      )}
    </YStack>
  )
}

const EventGroup = ({
  queryData,
  date,
  group,
  forceVertical,
}: {
  queryData: EventList_query$data
  date: string
  group: EventList_eventsConnection$data['edges']
  forceVertical?: boolean
}) => {
  const serverContext = useContext(ServerContext)

  const formattedDate = useMemo<{ relative: string; absolute: string }>(() => {
    const now = serverContext?.timeZone
      ? utcToZonedTime(new Date(), serverContext.timeZone)
      : new Date()

    const startAt = serverContext?.timeZone
      ? utcToZonedTime(new Date(group[0].node.startAt), serverContext.timeZone)
      : new Date(group[0].node.startAt)

    const dayDiff = differenceInCalendarDays(startAt, now)

    if (dayDiff === 0) {
      return {
        relative: 'Today',
        absolute: format(startAt, 'MMM do'),
      }
    }

    if (dayDiff === 1) {
      return {
        relative: 'Tomorrow',
        absolute: format(startAt, 'MMM do'),
      }
    }

    if (dayDiff > 0 && dayDiff < 7) {
      return {
        relative: format(startAt, 'eeee'),
        absolute: format(startAt, 'MMM do'),
      }
    }

    const weekDiff = differenceInCalendarWeeks(startAt, now)

    if (weekDiff === 1) {
      return {
        relative: format(startAt, "'Next week' eeee"),
        absolute: format(startAt, 'MMM do'),
      }
    }

    const yearDiff = differenceInCalendarYears(startAt, now)

    if (yearDiff === 0) {
      return {
        relative: format(startAt, 'eeee'),
        absolute: format(startAt, 'MMM do'),
      }
    }

    return {
      relative: format(startAt, 'eeee'),
      absolute: format(startAt, 'MMM do, yyyy'),
    }
  }, [group[0].node.startAt])

  return (
    <YStack key={date} gap='$6'>
      <XStack alignItems='center' gap='$4'>
        <XStack gap='$3'>
          <Strong color='$color'>{formattedDate.relative}</Strong>

          <Strong color='$colorMuted'>{formattedDate.absolute}</Strong>
        </XStack>

        <Separator borderColor='$borderColor' />
      </XStack>

      <YStack gap='$6'>
        {group.map((edge) => {
          return (
            <EventItem
              key={edge.cursor}
              event={edge.node}
              query={queryData}
              forceVertical={forceVertical}
            />
          )
        })}
      </YStack>
    </YStack>
  )
}
